import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { OrgRole } from '../lib/api';

interface NavItem {
  to: string;
  label: string;
  minRole: OrgRole;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/',              label: 'Dashboard',     minRole: 'member' },
  { to: '/approvals',     label: 'Approvals',     minRole: 'admin'  },
  { to: '/members',       label: 'Members',       minRole: 'admin'  },
  { to: '/groups',        label: 'Groups',        minRole: 'admin'  },
  { to: '/teams',         label: 'Teams',         minRole: 'admin'  },
  { to: '/question-bank', label: 'Question Bank', minRole: 'admin'  },
  { to: '/insights',      label: 'Insights',      minRole: 'admin'  },
  { to: '/moderation',    label: 'Moderation',    minRole: 'admin'  },
  { to: '/settings',      label: 'Settings',      minRole: 'admin'  },
  { to: '/billing',       label: 'Billing',       minRole: 'owner'  },
  { to: '/audit-log',     label: 'Audit Log',     minRole: 'admin'  },
];

const ROLE_LEVEL: Record<OrgRole, number> = { member: 0, admin: 1, owner: 2 };

export function Layout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const visibleItems = NAV_ITEMS.filter(
    (item) =>
      user && ROLE_LEVEL[user.role] >= ROLE_LEVEL[item.minRole],
  );

  async function handleSignOut() {
    await signOut();
    navigate('/sign-in');
  }

  return (
    <div style={{ display: 'flex', minHeight: '100dvh' }}>
      {/* Sidebar */}
      <nav
        aria-label="Main navigation"
        style={{
          width: 'var(--sidebar-width)',
          background: 'var(--color-sidebar-bg)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          height: '100dvh',
          overflowY: 'auto',
        }}
      >
        {/* Brand */}
        <div
          style={{
            padding: 'var(--space-6) var(--space-5)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: '1.125rem',
              color: 'var(--color-sidebar-text)',
              letterSpacing: '-0.01em',
            }}
          >
            Anchor
          </span>
          {user && (
            <div
              style={{
                fontSize: '0.75rem',
                color: 'var(--color-sidebar-muted)',
                marginTop: 'var(--space-1)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.orgName}
            </div>
          )}
        </div>

        {/* Nav links */}
        <ul
          role="list"
          style={{
            listStyle: 'none',
            padding: 'var(--space-3) var(--space-2)',
            flex: 1,
          }}
        >
          {visibleItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                style={({ isActive }) => ({
                  display: 'block',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius)',
                  fontSize: '0.9375rem',
                  fontWeight: isActive ? 600 : 400,
                  textDecoration: 'none',
                  color: isActive
                    ? '#ffffff'
                    : 'var(--color-sidebar-muted)',
                  background: isActive
                    ? 'var(--color-sidebar-active)'
                    : 'transparent',
                  marginBottom: '2px',
                  transition: 'background var(--transition-fast), color var(--transition-fast)',
                })}
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* User info + sign out */}
        {user && (
          <div
            style={{
              padding: 'var(--space-4) var(--space-5)',
              borderTop: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--color-sidebar-text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.displayName}
            </div>
            <div
              style={{
                fontSize: '0.75rem',
                color: 'var(--color-sidebar-muted)',
                marginTop: '2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.email}
            </div>
            <button
              onClick={handleSignOut}
              className="btn btn--ghost btn--sm"
              style={{
                marginTop: 'var(--space-3)',
                color: 'var(--color-sidebar-muted)',
                padding: '4px 0',
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main
        style={{
          flex: 1,
          minWidth: 0,
          padding: 'var(--space-8)',
          maxWidth: '1100px',
        }}
      >
        <Outlet />
      </main>
    </div>
  );
}
